
import Form from '@/ui-kit/components/form';
import FormField from '@/ui-kit/components/form/form-field';
import Gutter from '@/ui-kit/components/gutter';
import Button from '@/ui-kit/components/button';
import Icon from '@/ui-kit/components/icon';
import { FormFieldProperty } from '@/ui-kit/components/form/enums';
import { ControlType, ControlEventType } from '@/ui-kit/components/form/controls/enums';
import { TextInputProperty, TextInputType } from '@/ui-kit/components/form/controls/components/text-input/enums';
import { InputFillType, InputSlotType } from '@/ui-kit/components/form/controls/shared/input/enums';
import {
  StylePositionType,
  StyleSizeType,
  StyleColorSchemeType,
  StyleFontWeightType,
} from '@/ui-kit/styles/enums';
import { ButtonFillType, ButtonProperty, ButtonPropsEventType } from '@/ui-kit/components/button/enums';
import { IconProperty, IconType } from '@/ui-kit/components/icon/enums';
import { DatePickerProperty } from '@/ui-kit/components/form/controls/components/date-picker/enums';
import { TimePickerProperty } from '@/ui-kit/components/form/controls/components/time-picker/enums';
import { SelectProperty } from '@/ui-kit/components/form/controls/components/select/enums';
import Typography from '@/ui-kit/components/typography';
import { TypographyType } from '@/ui-kit/components/typography/enums';
import { ListingBookViewingFormControlName } from './enums';
import {
  dateControl,
  startTimeControl,
  additionalDateControl,
  additionalStartTimeControl,
  additionalDurationControl,
  listingBookViewingFormGroup,
} from './controls';
import {
  CONTACT_METHOD_OPTIONS,
  DURATION_OPTIONS,
} from './constants';
import { ListingBookViewingEvent } from '@/constants/AnalyticEvents';
import GetAvailableHoursRange from '@/mixins/forms/GetAvailableHoursRange';
import { phoneFormValidator } from '@/ui-kit/components/form/validators';

export default {
  name: 'ListingBookViewingForm',
  components: {
    Icon,
    Button,
    Gutter,
    Form,
    FormField,
    Typography,
  },
  mixins: [GetAvailableHoursRange],
  props: {
    listingId: {
      default: '',
      type: String,
    },
  },
  data() {
    return {
      TypographyType,
      StyleFontWeightType,
      StylePositionType,
      StyleSizeType,
      StyleColorSchemeType,
      FormFieldProperty,
      ControlType,
      ButtonFillType,
      ButtonProperty,
      ButtonPropsEventType,
      IconType,
      IconProperty,
      InputSlotType,
      ControlEventType,
      ListingBookViewingFormControlName,
      hasAdditionalTime: false,
      formGroup: listingBookViewingFormGroup,
    };
  },
  computed: {
    dateInputProps() {
      return {
        [DatePickerProperty.Name]: ListingBookViewingFormControlName.Date,
        [DatePickerProperty.FillType]: InputFillType.Outlined,
        [DatePickerProperty.MinDate]: new Date().setMinutes(0),
        [DatePickerProperty.Label]: 'Date',
        [DatePickerProperty.Placeholder]: 'Select a date',
      };
    },
    additionalDateInputProps() {
      return {
        ...this.dateInputProps,
        [DatePickerProperty.Name]: ListingBookViewingFormControlName.AdditionalDate,
      };
    },
    timeInputProps() {
      return {
        [TimePickerProperty.Name]: ListingBookViewingFormControlName.StartTime,
        [TimePickerProperty.FillType]: InputFillType.Outlined,
        [TimePickerProperty.MinuteInterval]: 30,
        [TimePickerProperty.HourRange]: this.getAvailableHoursRange(dateControl.value),
        [TimePickerProperty.Label]: 'Start Time',
        [TimePickerProperty.Placeholder]: 'Select a time',
      };
    },
    additionalTimeInputProps() {
      return {
        ...this.timeInputProps,
        [TimePickerProperty.Name]: ListingBookViewingFormControlName.AdditionalStartTime,
        [TimePickerProperty.HourRange]: this.getAvailableHoursRange(additionalDateControl.value),
      };
    },
    durationInputProps() {
      return {
        [SelectProperty.Name]: ListingBookViewingFormControlName.Duration,
        [SelectProperty.FillType]: InputFillType.Outlined,
        [SelectProperty.Label]: 'Duration',
        [SelectProperty.Placeholder]: 'Select duration',
        [SelectProperty.Options]: DURATION_OPTIONS,
      };
    },
    additionalDurationInputProps() {
      return {
        ...this.durationInputProps,
        [SelectProperty.Name]: ListingBookViewingFormControlName.AdditionalDuration,
      };
    },
    phoneNumberInputProps() {
      return {
        [TextInputProperty.Name]: ListingBookViewingFormControlName.PhoneNumber,
        [TextInputProperty.FillType]: InputFillType.Outlined,
        [TextInputProperty.TextType]: TextInputType.Telephone,
        [TextInputProperty.Label]: 'Phone Number',
        [TextInputProperty.Placeholder]: '(555) 555-5555',
      };
    },
    contactMethodInputProps() {
      return {
        [SelectProperty.Name]: ListingBookViewingFormControlName.ContactMethod,
        [SelectProperty.FillType]: InputFillType.Outlined,
        [SelectProperty.Label]: 'Contact method',
        [SelectProperty.Placeholder]: 'Select preferred contact method',
        [SelectProperty.Options]: CONTACT_METHOD_OPTIONS,
      };
    },
    additionalTimeToggleText() {
      return this.hasAdditionalTime
        ? 'Remove Another Time'
        : 'Add Another Time';
    },
    additionalTimeToggleIcon() {
      return this.hasAdditionalTime
        ? IconType.Minus
        : IconType.Add;
    },
  },
  methods: {
    trackEvent(eventName) {
      this.$track(eventName, { listingId: this.listingId });
    },
    handleDateChange() {
      this.trackEvent(ListingBookViewingEvent.MLSViewingRequestDate);
      startTimeControl.reset();
    },
    handlePhoneChange(value) {
      const isValidPhone = phoneFormValidator.getIsValid(value);

      if (isValidPhone) {
        this.trackEvent(ListingBookViewingEvent.MLSViewingRequestRequestPhoneNumber);
      }
    },
    handleContactChange() {
      this.trackEvent(ListingBookViewingEvent.MLSViewingRequestContact);
    },
    handleDurationChange() {
      this.trackEvent(ListingBookViewingEvent.MLSViewingRequestDuration);
    },
    handleTimeBlur() {
      this.trackEvent(ListingBookViewingEvent.MLSViewingRequestStartTime);
    },
    handleAdditionalDateChange() {
      additionalStartTimeControl.reset();
    },
    handleClickAdditionalTimeToggle() {
      this.hasAdditionalTime = !this.hasAdditionalTime;

      this.trackEvent(this.hasAdditionalTime
        ? ListingBookViewingEvent.MLSViewingRequestAdditionalTimeLink
        : ListingBookViewingEvent.MLSViewingRequestRemoveDataLink);

      if (this.hasAdditionalTime) {
        this.addAdditionalTimeControls();

        return;
      }

      this.removeAdditionalTimeControls();
    },
    addAdditionalTimeControls() {
      this.formGroup.addControls({
        [ListingBookViewingFormControlName.AdditionalDate]: additionalDateControl,
        [ListingBookViewingFormControlName.AdditionalStartTime]: additionalStartTimeControl,
        [ListingBookViewingFormControlName.AdditionalDuration]: additionalDurationControl,
      });
    },
    removeAdditionalTimeControls() {
      this.formGroup.removeControls([
        ListingBookViewingFormControlName.AdditionalDate,
        ListingBookViewingFormControlName.AdditionalStartTime,
        ListingBookViewingFormControlName.AdditionalDuration,
      ]);
    },
  },
};
